/**
 * "אפשר התראות" banner for iOS PWA users.
 *
 * iOS standalone PWAs silently fail when `Notification.requestPermission()`
 * is called outside a user gesture. So the bootstrap can't trigger the
 * prompt — the user must tap something. This banner is that tap.
 *
 * Shows ONLY when ALL hold:
 *   - In standalone PWA mode (iOS or otherwise — Android Chrome PWA also
 *     benefits from explicit consent).
 *   - `Notification.permission === 'default'` (never decided yet).
 *   - User hasn't dismissed for the current session.
 */

import { createLogger } from '../../core/logger.js';
import * as bus from '../../core/eventBus.js';
import * as push from './push.service.js';

const log = createLogger('push-permission-banner');

const DISMISS_KEY = 'c2c_push_permission_banner_dismissed_session';

let scheduled = false;

export function init() {
  if (scheduled) return;
  scheduled = true;
  bus.on('auth:ready', () => {
    setTimeout(() => { void maybeShow(); }, 3000);
  });
}

function shouldShow() {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'default') return false;
  if (!isStandalone()) return false;
  try {
    if (sessionStorage.getItem(DISMISS_KEY) === '1') return false;
  } catch { /* ignore */ }
  return true;
}

function isStandalone() {
  const navAny = /** @type {any} */ (navigator);
  if (navAny.standalone === true) return true;
  try { return window.matchMedia('(display-mode: standalone)').matches; }
  catch { return false; }
}

async function maybeShow() {
  if (!shouldShow()) return;
  log.info('showing notification permission banner');
  renderBanner();
}

function renderBanner() {
  const root = document.createElement('div');
  root.id = 'push-permission-banner';
  root.innerHTML = `
    <div class="ppb-card">
      <div class="ppb-icon">🔔</div>
      <div class="ppb-text">
        <strong>קבל התראות בזמן אמת</strong>
        <span>הודעות חדשות מנהגים אחרים, תוקף רישיון, ועוד.</span>
      </div>
      <div class="ppb-actions">
        <button class="ppb-allow" type="button">אפשר</button>
        <button class="ppb-dismiss" type="button" aria-label="סגור">✕</button>
      </div>
    </div>
  `;
  injectStyles();
  document.body.appendChild(root);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    root.remove();
  };

  root.querySelector('.ppb-dismiss')?.addEventListener('click', dismiss);
  root.querySelector('.ppb-allow')?.addEventListener('click', async () => {
    const btn = /** @type {HTMLButtonElement | null} */ (root.querySelector('.ppb-allow'));
    if (btn) { btn.disabled = true; btn.textContent = '...'; }
    try {
      const result = await push.requestPermissionAndRegister();
      log.info('permission result:', result);
    } catch (e) {
      log.warn('requestPermissionAndRegister threw:', e);
    }
    // Whether granted or denied — we're done here either way.
    dismiss();
  });
}

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    #push-permission-banner {
      position: fixed;
      top: env(safe-area-inset-top, 0);
      inset-inline: 12px;
      z-index: 99997;
      direction: rtl;
      animation: ppb-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }
    @keyframes ppb-in {
      from { transform: translateY(-20px); opacity: 0; }
      to   { transform: translateY(0); opacity: 1; }
    }
    #push-permission-banner .ppb-card {
      display: flex; align-items: center; gap: 12px;
      background: #ffffff;
      border-radius: 16px;
      padding: 12px 14px;
      margin-top: 8px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
      border: 1px solid #e2e8f0;
    }
    #push-permission-banner .ppb-icon {
      font-size: 28px; line-height: 1; flex-shrink: 0;
    }
    #push-permission-banner .ppb-text {
      flex: 1; display: flex; flex-direction: column; gap: 2px;
      font-size: 13px; line-height: 1.35;
    }
    #push-permission-banner .ppb-text strong {
      color: #0f172a; font-weight: 700;
    }
    #push-permission-banner .ppb-text span {
      color: #64748b; font-size: 12px;
    }
    #push-permission-banner .ppb-actions {
      display: flex; align-items: center; gap: 6px; flex-shrink: 0;
    }
    #push-permission-banner .ppb-allow {
      background: linear-gradient(135deg, #14b8a6 0%, #6366f1 100%);
      color: #fff; border: 0; border-radius: 10px;
      padding: 8px 14px; font: inherit; font-weight: 700; font-size: 13px;
      cursor: pointer;
    }
    #push-permission-banner .ppb-allow:disabled { opacity: 0.6; cursor: wait; }
    #push-permission-banner .ppb-dismiss {
      background: transparent; border: 0;
      color: #94a3b8; font-size: 14px; cursor: pointer;
      width: 28px; height: 28px; border-radius: 50%;
    }
    #push-permission-banner .ppb-dismiss:hover { background: #f1f5f9; color: #475569; }
  `;
  document.head.appendChild(style);
}
